import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { BottomSheet } from '@xing-com/bottom-sheet';
import { TextButton } from '@xing-com/button';
import { IconFilter, IconReturn } from '@xing-com/icons';
import { NotOnLarge } from '@xing-com/layout-primitives';
import { Tag } from '@xing-com/tag';

import { useSearchStore } from '../../context';
import {
  MobileAppliedFiltersContainer,
  MobileFiltersContainer,
  MobileFiltersScrollable,
  MobileFiltersSheetContainer,
  MobileFiltersSheetHeader,
} from '../shared-styles';
import { ShowResultsButton } from '../show-results-button';

type AggregatedFilterComponent = Array<{
  key: string;
  title?: string;
  quickMenu: boolean;
  isSelected: boolean;
  component: React.ReactNode;
}>;

type MobileFiltersProps = {
  aggregatedFilterComponents?: AggregatedFilterComponent;
  appliedFiltersComponent?: React.ReactNode;
};

export const MobileFilters: React.FC<MobileFiltersProps> = ({
  aggregatedFilterComponents,
  appliedFiltersComponent,
}) => {
  const { formatMessage } = useIntl();
  const resetFilters = useSearchStore((store) => store.resetFilters);
  const searchParams = useSearchStore((store) => store.searchParams);
  const count = useSearchStore((store) => store.count);
  const [showBottomSheet, setShowBottomSheet] = useState<
    boolean | { focus: string }
  >(false);

  const showAppliedFiltersComponent =
    showBottomSheet === true &&
    searchParams &&
    Object.keys(searchParams)
      .filter((key) => key !== 'sort')
      .filter((key) => key !== 'keywords').length > 0;

  return (
    <NotOnLarge>
      <MobileFiltersContainer data-testid="mobile-filters-container">
        <Tag
          leadingIcon={IconFilter}
          hideBorder
          onClick={() => setShowBottomSheet(true)}
        />
        <MobileFiltersScrollable>
          {aggregatedFilterComponents
            ?.filter(({ quickMenu }) => quickMenu)
            .map(({ key, isSelected, title }) => {
              return (
                <Tag
                  key={key}
                  selected={isSelected}
                  hideBorder
                  onClick={() => setShowBottomSheet({ focus: key })}
                >
                  {title}
                </Tag>
              );
            })}
          <TextButton onClick={resetFilters} icon={IconReturn} size="small">
            {formatMessage({ id: 'SE_FILTERS_RESET_ACTION' })}
          </TextButton>
        </MobileFiltersScrollable>
      </MobileFiltersContainer>
      <BottomSheet
        dimmerTitle="close"
        onOutsideClick={() => setShowBottomSheet(false)}
        show={showBottomSheet !== false}
      >
        <MobileFiltersSheetContainer>
          {showBottomSheet === true && (
            <MobileFiltersSheetHeader size="xxlarge">
              <FormattedMessage id="SE_SEARCH_MOBILE_FILTERS_TITLE" />
            </MobileFiltersSheetHeader>
          )}
          {showAppliedFiltersComponent && (
            <MobileAppliedFiltersContainer>
              {appliedFiltersComponent}
            </MobileAppliedFiltersContainer>
          )}
          {aggregatedFilterComponents
            ?.filter(
              ({ key }) =>
                showBottomSheet === true ||
                (typeof showBottomSheet === 'object' &&
                  showBottomSheet.focus === key)
            )
            .map(({ component }) => component)}
        </MobileFiltersSheetContainer>
        {count > 0 ? (
          <ShowResultsButton setShowBottomSheet={setShowBottomSheet} />
        ) : null}
      </BottomSheet>
    </NotOnLarge>
  );
};
